import {
  cn,
  Table,
  TableBody,
  TableCell,
  TableEmptyState,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from '@hub-la/shadcn'
import isEmpty from 'lodash/isEmpty'
import times from 'lodash/times'
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react'
import moment from 'moment'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Partner } from '../../../domain/dtos/partner'
import { OrderDirection } from '../../../domain/enums/order-direction'
import { PartnershipActions } from '../../components/partnership-actions'
import { useListPartnerships } from '../../hooks/use-list-partnerships'
import { PartnershipStatusTag } from './partnership-status-tag'
import { PartnershipsTableLoading } from './partnerships-table-loading'
import { useRefetch } from '../../hooks/use-refetch'

interface PartnershipsTableProps {
  filters: any
}

export const PartnershipsTable: React.FC<PartnershipsTableProps> = ({ filters }) => {
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(25)
  const [orderBy, setOrderBy] = useState<string>('createdAt')
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC)

  const { t } = useTranslation()
  const { search, statuses: status } = filters
  const {
    data = { items: [], lastPage: 0, page: 1, pageSize: 1, total: 0 },
    isFetching,
    refetch,
  } = useListPartnerships({
    page,
    pageSize,
    orderBy,
    orderDirection,
    filters: {
      search,
      status: status.length ? status : undefined,
    },
  })

  const handleRequestSort = useCallback(
    (property: string) => {
      const isAsc = orderBy === property && orderDirection === OrderDirection.ASC
      setOrderDirection(isAsc ? OrderDirection.DESC : OrderDirection.ASC)
      setOrderBy(property)
    },
    [orderDirection, orderBy],
  )

  const headCells = [
    { key: 'product', label: 'Produto', sortable: false, width: 190 },
    { key: 'status', label: 'Status', sortable: false, width: 90 },
    { key: 'commissioning', label: 'Comissão', sortable: false, width: 60 },
    { key: 'expires', label: 'Data de encerramento', sortable: false, width: 90 },
    { key: 'createdAt', label: 'Data de criação', sortable: true, width: 110 },
    { key: 'actions', label: '', sortable: false, width: 80 },
  ]

  const isRowsEmpty = isEmpty(data.items)
  const canRenderEmptyState = !isFetching && isRowsEmpty

  useRefetch({ ...filters }, refetch)

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div className="flex flex-col gap-4  bg-background rounded-md border ">
        <Table>
          <TableHeader>
            <TableRow>
              {headCells.map((cell) => (
                <TableHead
                  key={cell.key}
                  style={{ width: cell.width }}
                  className={cn('font-medium', { 'cursor-pointer': cell.sortable })}
                  onClick={cell.sortable ? () => handleRequestSort(cell.key) : undefined}
                >
                  <div className="flex items-center">
                    {cell.label}

                    {cell.sortable &&
                      (cell.key === orderBy ? (
                        orderDirection === OrderDirection.ASC ? (
                          <ChevronUp className="ml-2 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-2 h-4 w-4" />
                        )
                      ) : (
                        <ChevronsUpDown className="ml-2 h-4 w-4" />
                      ))}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {isFetching
              ? times(pageSize, (i) => <PartnershipsTableLoading key={`skeleton-row-${i}`} />)
              : data.items.map((partner: Partner) => (
                  <TableRow key={partner.id}>
                    <TableCell>
                      <div className="flex items-center space-x-4">
                        <img
                          src={partner.offer.picture ?? '/placeholder-image.svg'}
                          alt={partner.offer.name}
                          className="w-[61px] h-auto aspect-[218/142] rounded object-cover"
                        />

                        <div>
                          <p className="text-base font-medium">{partner.offer.name}</p>
                          <p className="text-sm text-muted-foreground">{partner.offer.id}</p>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <PartnershipStatusTag status={partner.status} />
                    </TableCell>

                    <TableCell>{partner.commission.sell?.percent ?? 0}%</TableCell>

                    <TableCell>{partner.expiresAt ? moment(partner.expiresAt).format('D MMM. YYYY') : '∞'}</TableCell>

                    <TableCell>{moment(partner.createdAt).format('D MMM. YYYY')}</TableCell>

                    <TableCell>
                      <PartnershipActions
                        receiverId={partner.id}
                        status={partner.status}
                        termsText={partner.termsText}
                        commission={partner.commission.sell?.percent ?? 0}
                        expiresAt={partner.expiresAt}
                        onChange={refetch}
                      />
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>

        {canRenderEmptyState && <TableEmptyState title={t('empty')} />}
      </div>

      {!isRowsEmpty && (
        <TablePagination
          page={page}
          setPage={setPage}
          lastPage={data.lastPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  )
}
